import {
  ActivatedRouteSnapshot,
  DetachedRouteHandle,
  RouteReuseStrategy, UrlSegment
} from '@angular/router';
import { Component, ComponentRef, Injectable, Type } from "@angular/core";
import { LMContextService, LMRouteComponentService, LMSpecService, LMUrlUtilService } from './services';
import {
    LMBaseComponent, LMFacadeExplorerComponent,
    LMFormComponent,
    LMListComponent,
    LMPageComponent,
    LMReportsCatalogComponent,
    LoadResponse
} from '@app/components';
import { environment } from '@environments/environment';
import { AppConstants } from '@app/utils';
import { LMRouteReuseCacheService } from '@services/lm-route-reuse-cache.service';
import { LMRouteReuse } from '@base/lm-route-reuse';

@Injectable()
export class LMRouteReuseStrategy extends RouteReuseStrategy {
  /**
   * Determines if this route (and its subtree) should be detached to be reused later
   */


//  private routeCache = new Map<string, DetachedData>();
  private futureComp: Component | undefined;

  constructor(
    private currentComponent: LMRouteComponentService,
    private lmUrlUtilService: LMUrlUtilService,
    private lmSpecService: LMSpecService,
    private lmContextService: LMContextService,
    private lmRouteReuseCache: LMRouteReuseCacheService
  ) {
    super();
  }

  shouldDetach(route: ActivatedRouteSnapshot): boolean {
    const currentRouteComponent = this.lmRouteReuseCache.getCompFromRoute(route);
    if (currentRouteComponent instanceof LMFacadeExplorerComponent) {
      return true;
    }

    if (!this.lmContextService.isRouteReuse())
      return false;

    // const currentComponent: Component = this.currentComponent.getRouteComponent();
    try {
      if (!(currentRouteComponent as LMRouteReuse).allowRouteReuseSave()){
        return false;
      }
    } catch (e) {
      // noop
    }


    return this.lmRouteReuseCache.isReusableContent(route);
  }

  /**
   * Stores the detached route
   */
  store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle): void {
    const currentRouteComponent = this.lmRouteReuseCache.getCompFromRoute(route);

    this.setRouteReuseActive(currentRouteComponent,false, '');
    const key: string = this.lmRouteReuseCache.getKeyFromRoute(route);

    this.lmRouteReuseCache.addRouteItem(key, handle, route);
    // if (!environment.production) {
    //   console.log("**ROUTEREUSE** Rough Memory used for route reuse cashe in " + this.roughSizeOfObject(this.routeCache));
    // }
  }

  // **************************************************
  // there is no guarentee that this method id called only once on a perticular route reload.
  // https://github.com/angular/angular/issues/43251
  // part of a response to a bug written and closed on this issue is below
  // There are no guarantees from a public API standpoint as to how many times the router will call these methods.
  // so code must be dded to only run the code once after a route has been reused to avoid situation like data reloading ore than once.
  /**
   * Determines if this route (and its subtree) should be reattached
   */
  shouldAttach(route: ActivatedRouteSnapshot): boolean {
    const currentRouteComponent = this.lmRouteReuseCache.getCompFromRoute(route);

    if (!(currentRouteComponent instanceof LMFacadeExplorerComponent)) {
        if (!this.lmContextService.isRouteReuse())
            return false;
    }

    if (this.lmContextService.getClearPageState()) {
      this.lmRouteReuseCache.removeRouteHandleFromCache(route);
      return false;
    }

    // Should this route be attached
    return (this.lmRouteReuseCache.isReusableContent(route) && this.lmRouteReuseCache.hasHandle(route));
  }

  /**
   * Retrieves the previously stored route
   */
  retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
    // const currentComponent: Component = this.currentComponent.getRouteComponent();
    let comp: Component = this.lmRouteReuseCache.getCompFromRoute(route);
    if (this.lmRouteReuseCache.isReusableContent(route)) {
      if (this.lmRouteReuseCache.hasHandle(route)) {
        // Fire the event tolet the app know that params might have changed
        setTimeout(() => {
          try {
//            this.initRouterComponent(comp);
            const recordResource: string = this.lmRouteReuseCache.getRecordResource(route);
            const recordKey: string = this.lmRouteReuseCache.getRecordKeysFromSegment(recordResource);

            this.setRouteReuseActive(comp, true, recordKey);
          } catch(e) {
            console.log('**ROUTEREUSE error storing route** ' + e);
          }

        }, 1);
        // if (!environment.production) {
        //   console.log('**ROUTEREUSE** Loading route key ' + this.getKeyFromRoute(route));
        // }
        return this.lmRouteReuseCache.getHandle(route);
      }
    }
    this.setRouteReuseActive(comp, true, '');
    return null;
  }

  /**
   * Determines if a route should be reused
   */
  shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
    // should the route coming in be reused from stored
    if (this.lmRouteReuseCache.isReusableContent(future) && this.lmRouteReuseCache.hasHandle(future)) {
      const comp = this.lmRouteReuseCache.getCompFromRoute(future);
      this.futureComp = comp;
      return true;
    }
    return false;
  }

  private getResolvedUrl(route: ActivatedRouteSnapshot): string {
    return route.pathFromRoot
    .map(v => v.url.map(segment => segment.toString()).join('/'))
    .join('/');
  }
  private getConfiguredUrl(route: ActivatedRouteSnapshot): string {
    return '/' + route.pathFromRoot
    .filter(v => v.routeConfig)
    .map(v => v.routeConfig!.path)
    .join('/');
  }

  private setRouteReuseActive(comp: Component, active: boolean, recordKey: string) {
    // if (comp instanceof LMReportsCatalogComponent) {
    //   return (comp as LMReportsCatalogComponent).setRouteReuseActive(active);
    // } else
    if (comp instanceof LMFormComponent) {
      return (comp as LMFormComponent).setRouteReuseActive(active, recordKey);
    } else if (comp instanceof LMListComponent) {
      return (comp as LMListComponent).setRouteReuseActive(active);
    } else if (comp instanceof LMFacadeExplorerComponent) {
      return (comp as LMFacadeExplorerComponent).setRouteReuseActive(active);
    }
    // else if (comp instanceof LMPageComponent) {
    //   return (comp as LMPageComponent).setRouteReuseActive(active);
    // }
    return undefined;
  }

   private roughSizeOfObject(obj: any) : number {
    let bytes = 0;

    obj.forEach((value: any) => {
      bytes += value.component.elementRef.nativeElement.outerHTML.length
    });

    return bytes;
  }
}

export interface DetachedData {
  timestamp: Number,
  handle: DetachedRouteHandle,
  component: Component,  // Look in future release to remove. Use ComponentRef from routecache example
  params: Params,
  count: number,
  enteredIndex: number,
  points: number
}
